import React, { useEffect, useState } from 'react';
import { reduxForm, Field, Form, InjectedFormProps } from 'redux-form';
import Fields from 'components/Fields';
import { Button } from 'components/Base';
import Router from 'components/Router/Router';
import Logo from 'assets/images/fintegrateLogo.png';
import { ReactComponent as GeometricBackground } from 'assets/images/geometricBackground.svg';
import { makeStyles } from '@material-ui/core';
import { useAuth } from 'providers/AuthProvider/SessionAuthProvider';
import Icons from 'components/Icons';
import classNames from 'classnames';
import api from 'api';
import { Notify } from 'utils';
import {
  VisibilityRounded as VisibilityRoundedIcon,
  VisibilityOffRounded as VisibilityOffRoundedIcon
} from '@material-ui/icons';
import posthog from 'posthog-js';

const { useHistory } = Router;

const useStyles = makeStyles({
  root: {
    background: 'linear-gradient(#fafafa 60%, #efefef 60%)',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  background: {
    position: 'fixed',
    width: '100%',
    bottom: '40%'
  },
  logoWrapper: {
    height: '200px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  logo: {
    maxWidth: '300px',
    maxHeight: '100%',
    height: 'auto',
    paddingTop: '2rem',
    margin: '0 auto'
  },
  box: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
    boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.25)',
    borderRadius: '25px',
    maxWidth: '500px',
    width: '100%'
  },
  form: {
    padding: '1rem'
  },
  link: {
    marginLeft: 'auto',
    color: '#5989c5',
    cursor: 'pointer',
    '&:hover': {
      color: '#36649b'
    },
    fontWeight: 600
  },
  flex: {
    display: 'flex',
    alignItems: 'center'
  },
  error: {
    color: '#ed4337'
  },
  addons: {
    fontSize: '14px',
    fontWeight: 600
  },
  textCenter: {
    textAlign: 'center'
  },
  inputField: {
    padding: '0.25rem 0'
  },
  subText: {
    color: '#6b7280',
    marginTop: '-1rem',
    fontSize: '0.9rem'
  },
  showPasswordIconWrapper: {
    position: 'absolute',
    bottom: '20px',
    right: 0
  }
});

const LoginPage = ({ handleSubmit }: InjectedFormProps) => {
  const [page, setPage] = useState('login');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [passwordResetRequested, setPasswordResetRequested] = useState(false);
  const [passwordResetEmail, setPasswordResetEmail] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const {
    login,
    getUser,
    getNewCsrfToken,
    setCsrfFromHeader,
    isAuthenticated,
    desiredPath
  } = useAuth();
  const history = useHistory();
  const classes = useStyles();

  const getError = (res: any) => {
    if (
      res.response.status === 403 &&
      res.response.data.message === 'Too many failed login attempts'
    ) {
      return 'Your account has been blocked after consecutive failed login attempts';
    }

    if (!res?.response || res.response.status >= 403) {
      return 'There was a problem logging in to your account. Please try again later.';
    }

    return 'Incorrect email or password';
  };

  const submit = (values: any) => {
    setSubmitting(true);
    getNewCsrfToken().then(() => {
      login(values)
        .then((res: any) => {
          getUser();

          if (res?.data?.id || res?.data?.email) {
            posthog.identify(res.data.id || res.data.email, {
              email: res.data.email,
              name: res.data.name
            });

            posthog.capture('user_logged_in');
          }
          setCsrfFromHeader(res);
        })
        .catch((res: any) => {
          setError(getError(res));
        })
        .finally(() => {
          setSubmitting(false);
        });
    });
  };

  const sendPasswordReset = (values: any) => {
    api.requestPasswordReset(values.email).then(() => {
      if (passwordResetRequested) {
        Notify.success('Password reset email resent');
      }
      setPasswordResetEmail(values.email);
      setPasswordResetRequested(true);
    });
  };

  useEffect(() => {
    if (isAuthenticated) {
      history.push(desiredPath || '/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return (
    <div className={classes.root}>
      <GeometricBackground className={classes.background} color="#efefef" />
      <div className={classes.box}>
        <div className={classes.logoWrapper}>
          <img className={classes.logo} src={Logo} alt="Fintegrate Logo" />
        </div>
        {page === 'login' && (
          <div className={classes.form}>
            <h2>Enter your login details below</h2>
            <Form onSubmit={handleSubmit(submit)}>
              <Field
                name="email"
                component={Fields.Text}
                className={classes.inputField}
                label="Email address"
                type="email"
                required
              />
              <div style={{ position: 'relative' }}>
                <Field
                  style={{ margin: '1rem 0', width: '100%' }}
                  name="password"
                  component={Fields.Text}
                  className={classes.inputField}
                  label="Password"
                  type={isPasswordVisible ? 'text' : 'password'}
                  autoComplete="off"
                  required
                />
                <div
                  onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                  className={classes.showPasswordIconWrapper}
                >
                  {isPasswordVisible ? (
                    <VisibilityRoundedIcon />
                  ) : (
                    <VisibilityOffRoundedIcon />
                  )}
                </div>
              </div>
              <div className={classNames(classes.addons, classes.flex)}>
                {error && (
                  <div className={classNames(classes.error, classes.flex)}>
                    <Icons.ErrorIcon
                      style={{ marginRight: '0.25rem' }}
                      fill="#ed4337"
                    />
                    <div>{error}</div>
                  </div>
                )}
                <div
                  className={classes.link}
                  style={{ whiteSpace: 'nowrap' }}
                  onClick={() => setPage('reset-password')}
                >
                  Forgot password?
                </div>
              </div>

              <Button
                isLoading={submitting}
                type="submit"
                style={{ width: '100%', marginTop: '1rem' }}
              >
                Login
              </Button>
            </Form>
          </div>
        )}
        {page === 'reset-password' && (
          <div className={classes.form}>
            {!passwordResetRequested && (
              <>
                <h2>Reset your password</h2>
                <p className={classes.subText}>
                  We will email you a link to reset your password.
                </p>
                <Form name="reset" onSubmit={handleSubmit(sendPasswordReset)}>
                  <Field
                    name="email"
                    component={Fields.Text}
                    className={classes.inputField}
                    label="Email address"
                    type="email"
                    required
                  />

                  <Button
                    type="submit"
                    style={{
                      width: '100%',
                      marginTop: '1rem',
                      marginBottom: '1rem'
                    }}
                  >
                    Send me an email
                  </Button>

                  <div
                    style={{ marginTop: '0.5rem' }}
                    className={classNames(
                      classes.link,
                      classes.textCenter,
                      classes.addons
                    )}
                    onClick={() => setPage('login')}
                  >
                    Return to Login
                  </div>
                </Form>
              </>
            )}
            {passwordResetRequested && (
              <>
                <h2>Thanks!</h2>
                <p>
                  If {passwordResetEmail} is an existing user account, you will
                  receive an email shortly containing further instructions to
                  reset your password.
                </p>
                <p>
                  If you haven't received an email in the next 5 minutes, please
                  check your spam,{' '}
                  <span
                    className={classes.link}
                    onClick={() => {
                      sendPasswordReset({ email: passwordResetEmail });
                    }}
                  >
                    resend
                  </span>
                  {' or '}
                  <span
                    className={classes.link}
                    onClick={() => setPasswordResetRequested(false)}
                  >
                    try a different email address
                  </span>
                  .
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default reduxForm({
  form: 'login',
  enableReinitialize: true
})(LoginPage);
